import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Tag,
  TagLabel,
  TagCloseButton,
  Checkbox,
  Divider,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useColorModeValue,
  Flex,
  Progress,
  Badge
} from '@chakra-ui/react';
import JobDescriptionEditor from 'components/Commoncomponent/JobDescriptionEditor/JobDescriptionEditor';
import { useCookies } from 'react-cookie';

const MachedCandidateEmailModal = ({ candidates = [], isOpen, onClose, onSendEmails }) => {
  const [cookie] = useCookies(["role", "id", "token"]);
  const userId = cookie.id;
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [includeButton, setIncludeButton] = useState(false);
  const [buttonTitle, setButtonTitle] = useState('');
  const [buttonLink, setButtonLink] = useState('');
  const [emailSignature, setEmailSignature] = useState('');

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleCandidateSelect = (candidate, isChecked) => {
    if (isChecked) {
      setSelectedCandidates([candidate, ...selectedCandidates]);
    } else {
      setSelectedCandidates(
        selectedCandidates.filter(selected => selected._id !== candidate._id)
      );
    }
  };

  const handleRemoveCandidate = (candidateToRemove) => {
    setSelectedCandidates(
      selectedCandidates.filter(candidate => candidate._id !== candidateToRemove._id)
    );
  };

  const isCandidateSelected = (candidate) => {
    return selectedCandidates.some(selected => selected._id === candidate._id);
  };

  const handleSelectAll = () => {
    setSelectedCandidates([...candidates]);
  };

  const handleDeselectAll = () => {
    setSelectedCandidates([]);
  };

  const handleNextStep = () => {
    if (currentStep === 1 && selectedCandidates.length > 0) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSendEmails = () => {
    if (onSendEmails && selectedCandidates.length > 0 && emailSubject && emailBody) {
      const emailData = {
        candidates: selectedCandidates,
        subject: emailSubject,
        body: emailBody,
        button: includeButton ? {
          title: buttonTitle,
          link: buttonLink
        } : null,
        signature: emailSignature,
        owner: userId
      };
      onSendEmails(emailData);
      // handleClose();
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedCandidates([]);
    setEmailSubject('');
    setEmailBody('');
    setIncludeButton(false);
    setButtonTitle('');
    setButtonLink('');
    setEmailSignature('');
    onClose();
  };

  const isStep2Valid = emailSubject.trim() !== '' && emailBody.trim() !== '' &&
    (!includeButton || (buttonTitle.trim() !== '' && buttonLink.trim() !== ''));
  useEffect(() => {
    candidates && setSelectedCandidates([...candidates]);
  }, [candidates]);
  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent bg={bgColor} maxW="900px">
        <ModalHeader>
          <VStack align="stretch" spacing={3}>
            <Text>
              {currentStep === 1
                ? "Step 1: Select Candidates"
                : "Step 2: Create Email Template"}
            </Text>
            <Box>
              <HStack spacing={2} mb={2}>
                <Text fontSize="sm" fontWeight="normal" color="gray.500">
                  Step {currentStep} of 2
                </Text>
              </HStack>
              <Progress
                value={(currentStep / 2) * 100}
                size="sm"
                colorScheme="blue"
              />
            </Box>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {currentStep === 1 ? (
            // Step 1: Candidate Selection
            <VStack spacing={4} align="stretch">
              <Box>
                <HStack justify="space-between" align="center" mb={3}>
                  <Text fontWeight="semibold">
                    Available Candidates ({candidates.length})
                  </Text>
                  <Box display="flex" gap="10px">
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={handleDeselectAll}
                    >
                      Deselect All
                    </Button>
                  </Box>
                </HStack>
                <Box
                  maxH="250px"
                  overflowY="auto"
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  p={2}
                >
                  <VStack spacing={2} align="stretch">
                    {candidates.map((candidate, index) => (
                      <Box
                        key={`${candidate._id}-${index}`}
                        p={3}
                        borderRadius="md"
                        border="1px"
                        borderColor={borderColor}
                        _hover={{ bg: hoverBg }}
                        cursor="pointer"
                        onClick={() =>
                          handleCandidateSelect(
                            candidate,
                            !isCandidateSelected(candidate)
                          )
                        }
                      >
                        <HStack justify="space-between">
                          <Flex gap="8px" direction="column" flex={1}>
                            <Text fontWeight="medium">
                              {candidate.firstName} {candidate.lastName}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {candidate.email}
                            </Text>
                          </Flex>
                          <Checkbox
                            isChecked={isCandidateSelected(candidate)}
                            isDisabled
                            style={{ cursor: "pointer" }}
                            borderColor={borderColor}
                          />
                        </HStack>
                      </Box>
                    ))}
                    {candidates.length === 0 && (
                      <Text textAlign="center" color="gray.500" py={4}>
                        No candidates available
                      </Text>
                    )}
                  </VStack>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Text fontWeight="semibold" mb={3}>
                  Selected Candidates ({selectedCandidates.length})
                </Text>
                <Box
                  minH="120px"
                  maxH="200px"
                  overflowY="auto"
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  p={3}
                  bg={useColorModeValue("gray.50", "gray.700")}
                  gap={2}
                  display="flex"
                  flexWrap="wrap"
                >
                  {selectedCandidates.length > 0 ? (
                    selectedCandidates.map((candidate, index) => (
                      <Tag
                        key={`selected-${candidate._id}-${index}`}
                        size="md"
                        variant="solid"
                        colorScheme="blue"
                        mb={2}
                        height="24px"
                      >
                        <TagLabel>
                          {candidate.firstName} {candidate.lastName}
                        </TagLabel>
                        <TagCloseButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCandidate(candidate);
                          }}
                        />
                      </Tag>
                    ))
                  ) : (
                    <Text
                      color="gray.500"
                      textAlign="center"
                      width="100%"
                      py={8}
                    >
                      No candidates selected
                    </Text>
                  )}
                </Box>
              </Box>
            </VStack>
          ) : (
            // Step 2: Email Template Creation
            <VStack spacing={4} align="stretch">
              <Box
                bg={useColorModeValue("blue.50", "blue.900")}
                p={3}
                borderRadius="md"
              >
                <Text fontSize="sm" fontWeight="medium">
                  Sending to {selectedCandidates.length} candidate
                  {selectedCandidates.length !== 1 ? "s" : ""}
                </Text>
              </Box>

              <FormControl isRequired>
                <FormLabel>Email Subject</FormLabel>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>

              <Box>
                <Badge>$$name</Badge>
                {" "}for candidate name
              </Box>
              <Box>
                <Badge>$$email</Badge>
                {" "}for candidate email
              </Box>

              <FormControl isRequired>
                <FormLabel>Email Body</FormLabel>
                <JobDescriptionEditor
                  value={emailBody}
                  valueFunc={setEmailBody}
                />
              </FormControl>

              <Divider />

              <FormControl>
                <Checkbox
                  isChecked={includeButton}
                  onChange={(e) => setIncludeButton(e.target.checked)}
                >
                  <Text fontWeight="medium">Include Call-to-Action Button</Text>
                </Checkbox>
              </FormControl>

              {includeButton && (
                <Box pl={6} borderLeft="3px solid" borderColor="blue.500">
                  <VStack spacing={3} align="stretch">
                    <HStack spacing={3}>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm">Button Title</FormLabel>
                        <Input
                          value={buttonTitle}
                          onChange={(e) => setButtonTitle(e.target.value)}
                          placeholder="e.g., Apply Now"
                          size="sm"
                          bg={bgColor}
                          borderColor={borderColor}
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm">Button Link</FormLabel>
                        <Input
                          value={buttonLink}
                          onChange={(e) => setButtonLink(e.target.value)}
                          placeholder="e.g., https://example.com/apply"
                          size="sm"
                          bg={bgColor}
                          borderColor={borderColor}
                        />
                      </FormControl>
                    </HStack>
                  </VStack>
                </Box>
              )}

              <Divider />

              <FormControl>
                <FormLabel>Email Regards</FormLabel>
                <Input
                  value={emailSignature}
                  onChange={(e) => setEmailSignature(e.target.value)}
                  placeholder="Regards by"
                  rows={4}
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>

            {currentStep === 2 && (
              <Button variant="outline" onClick={handlePreviousStep}>
                Back
              </Button>
            )}

            {currentStep === 1 ? (
              <Button
                colorScheme="blue"
                onClick={handleNextStep}
                isDisabled={selectedCandidates.length === 0}
              >
                Create Template ({selectedCandidates.length})
              </Button>
            ) : (
              <Button
                colorScheme="blue"
                onClick={handleSendEmails}
                isDisabled={!isStep2Valid}
              >
                Send Emails ({selectedCandidates.length})
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MachedCandidateEmailModal;